// src/Components/TopBar/index.tsx
import { MenuIcon } from 'assets/icons';
import BirdLogoAOA from 'assets/logo/BirdHome.svg';

import { CustomButton } from 'Components/Auth/CustomButton';
import SideBarNav from 'Components/SideBarNav';

import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from 'utils/AuthProvider';

import ClientSupportButton from './ClientSupportButton';
import NotificationsBellButton from './NotificationsBellButton';
import ProfileButton from './ProfileButton';
import MeetingButton from './MeetingButton';
import SalaryCalculatorButton from './SalaryCalculatorButton';
import ChatbotButton from './ChatbotButton'; // AI Mito
import PendingActionsButton from './PendingActionsButton';
import AccountantQuickLinks from './AccountantQuickLinks'; // NEW
import styles from './style.module.css';

export interface ITopBarProps {}

export default function TopBar(props: ITopBarProps) {
  const location = useLocation();
  const { user, isClient, isAccountant } = useAuth();
  /* ------------------------------------------------------------------ */
  /* slide‑out sidebar toggle                                           */
  /* ------------------------------------------------------------------ */
  const [isNavOpen, setIsNavOpen] = useState<boolean>(false);
  useEffect(() => setIsNavOpen(false), [location]);

  /* ------------------------------------------------------------------ */
  /* helper to decide if “Login / Sign‑up” buttons must be shown        */
  /* ------------------------------------------------------------------ */
  function showLoginOptions() {
    const publicFolder = /(Folder|Document\/[1-9]+\/public)/;
    return publicFolder.test(location.pathname);
  }

  /* ------------------------------------------------------------------ */
  /* render                                                             */
  /* ------------------------------------------------------------------ */
  return (
    <div className={`md-max:z-10 ${styles.toolbarShadow}`}>
      <div className="mx-6 h-16 pt-6 pb-2 flex justify-between items-center">
        {/* ----------------------------------------------------------------
           Left : burger, logo, chatbot
        ---------------------------------------------------------------- */}
        <div className="flex flex-row justify-start items-center">
          {user && (
            <div
              className="lg-max:block hidden mr-3 cursor-pointer pt-2"
              onClick={() => setIsNavOpen(true)}
            >
              <MenuIcon />
            </div>
          )}

          <div className="flex items-center">
            <Link
              to={user ? '/dashboard' : '/login'}
              className="flex items-center flex-col"
            >
              <img
                src={BirdLogoAOA}
                alt="Bird Logo"
                className={`w-12 h-12 ${styles.birdLogo}`}
              />
            </Link>

            {!user && (
              <span className="ml-3 text-sm text-green font-medium">
                Welcome to Accountants on Air.
                <br />
                Less Taxes === More Savings!
              </span>
            )}
          </div>

          {/* hide AI‑Mito button on screens < 768 px */}
          {user && (
            <div className="hidden md:block ml-2">
              <ChatbotButton />
            </div>
          )}
        </div>

        {/* ----------------------------------------------------------------
           Right : app buttons (only when logged‑in)
        ---------------------------------------------------------------- */}
        {user && (
          <div className="flex items-center topbar-buttons">
            {/* NEW – one‑click “Pay / Sign / Finish” reminder */}
            {isAccountant && <AccountantQuickLinks />}
            {isClient && <PendingActionsButton />}

            <div className="hidden md:block">
              <SalaryCalculatorButton />
            </div>
            <MeetingButton />
            <NotificationsBellButton />
            <ClientSupportButton />
            <ProfileButton />
          </div>
        )}

        {/* ----------------------------------------------------------------
           Public‑folder routes : show Login / Sign‑up
        ---------------------------------------------------------------- */}
        {!user && showLoginOptions() && (
          <div className="flex items-center cursor-pointer text-gray-dark hover:text-opacity-90">
            <span className="w-28">
              <NavLink to="login">
                <CustomButton
                  textSize="text-sm"
                  text="Login"
                  textColor="green"
                  bgColor="transparent"
                  borderColor="green"
                  height="h-10"
                />
              </NavLink>
            </span>

            <span className="ml-4 w-28">
              <NavLink to="sign-up">
                <CustomButton
                  textSize="text-sm"
                  text="Sign Up"
                  bgColor="green"
                  borderColor="green"
                  height="h-10"
                />
              </NavLink>
            </span>
          </div>
        )}
      </div>

      {/* ----------------------------------------------------------------
         Slide‑out sidebar
      ---------------------------------------------------------------- */}
      <div
        className="h-full z-20 w-0 fixed top-0 left-0 overflow-x-hidden"
        style={{ transition: '0.5s', width: isNavOpen ? '100%' : '0' }}
      >
        <div className="flex flex-row justify-start h-full">
          <div className="w-3/5 bg-white p-9">
            <SideBarNav />
          </div>
          <div
            className="w-2/5 h-full bg-transparent"
            onClick={() => setIsNavOpen(false)}
          />
        </div>
      </div>

      {isNavOpen && (
        <div className="w-full h-full bg-black opacity-50 fixed z-10 top-0 left-0" />
      )}
    </div>
  );
}
